import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import SafeMuiDatatable from '../generic_components/SafeMuiDatatable';
import * as UTILS from '../utils/utilFunctions';
import TEXT from './text';

const columns = [
  {
    name: 'id',
    label: 'ID'
  },
  {
    name: 'status',
    label: 'Status',
    options: {
      filter: false,
      customBodyRender: status => {
        if (status === 'verfügbar') {
          return (
            <div>
              <i className="material-icons" style={{ color: 'green' }}>
                check_circle
              </i>
            </div>
          );
        }
        return (
          <div>
            <i className="material-icons " style={{ color: 'red' }}>
              remove_circle
            </i>
          </div>
        );
      }
    }
  },
  {
    name: 'salutation',
    label: 'Anrede',
    options: {
      filter: false
    }
  },
  {
    name: 'title',
    label: 'Titel',
    options: {
      filter: false
    }
  },
  {
    name: 'firstName',
    label: 'Vorname',
    options: {
      filter: false
    }
  },
  {
    name: 'lastName',
    label: 'Nachname',
    options: {
      filter: false
    }
  },
  {
    name: 'mailAddress',
    label: 'E-Mail',
    options: {
      filter: false
    }
  },
  {
    name: 'jobTitle',
    label: 'Berufsbezeichnung',
    options: {
      filter: false
    }
  },
  {
    name: 'company',
    label: 'Unternehmen',
    options: {
      filter: false
    }
  },
  {
    name: 'postalCode',
    label: 'PLZ',
    options: {
      filter: false
    }
  },
  {
    name: 'city',
    label: 'Stadt',
    options: {
      filter: false
    }
  },
  {
    name: 'dateOfBirth',
    label: 'Geburtsdatum',
    options: {
      filter: false,
      customBodyRender: value => (value ? UTILS.formatDateToDELocale(new Date(value)) : '')
    }
  },
  {
    name: 'lastUpdate',
    label: 'Zuletzt aktualisiert',
    options: {
      filter: false,
      customBodyRender: value => (value ? UTILS.formatDateToDELocale(new Date(value)) : '')
    }
  }
];

const muiTheme = {
  overrides: {
    MUIDataTableHeadCell: {
      root: {
        '&:hover': {
          cursor: 'pointer'
        }
      }
    },
    MUIDataTableBodyCell: {
      root: {
        '&:hover': {
          cursor: 'pointer'
        }
      }
    }
  }
};

/**
 * Component for selecting a duplicate candidate from a list
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Array} props.candidates - List of duplicate candidates
 * @param {Function} props.onSelect - Callback when candidate is selected
 * @param {Function} props.onCancel - Callback when dialog is cancelled
 */
const DuplicateCandidateSelector = ({ open, candidates, onSelect, onCancel }) => {
  const preparedData = useMemo(
    () =>
      candidates.map(candidate => [
        candidate.id || '',
        candidate.statusType || 'verfügbar',
        candidate.salutation || '',
        candidate.title || '',
        candidate.firstName || '',
        candidate.lastName || '',
        candidate.mailAddress || '',
        candidate.jobTitle || '',
        candidate.company || '',
        candidate.postalCode || '',
        candidate.city || '',
        candidate.dateOfBirth || '',
        candidate.lastUpdate || ''
      ]),
    [candidates]
  );

  const handleRowClick = rowData => {
    const candidateId = rowData[0];
    const selectedCandidate = candidates.find(c => c.id === candidateId);
    onSelect(selectedCandidate);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={false}>
      <DialogTitle>{TEXT.duplicateCandidateSelector.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {TEXT.duplicateCandidateSelector.description}
        </Typography>
        <SafeMuiDatatable
          tableName="duplicateCandidateSelector"
          columns={columns}
          data={preparedData}
          onRowClick={handleRowClick}
          muiTheme={muiTheme}
          options={{
            selectableRows: 'none',
            download: false,
            print: false,
            pagination: false
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="default">
          {TEXT.duplicateCandidateSelector.cancel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DuplicateCandidateSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  candidates: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default DuplicateCandidateSelector;
